import React, { createContext, useContext, useState, useEffect, useMemo } from "react";
import { ThemeProvider as MuiThemeProvider } from "@mui/material/styles";
import { createAppTheme } from "../../styles/theme";
import {
  THEME_MODES,
  THEME_STORAGE_KEY,
  FONT_SIZE_OPTIONS,
  FONT_SIZE_STORAGE_KEY,
} from "../../constants/theme";

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [darkMode, setDarkMode] = useState(() => {
    const savedTheme = localStorage.getItem(THEME_STORAGE_KEY);
    return savedTheme === THEME_MODES.DARK;
  });
  const [fontSize, setFontSize] = useState(() => {
    const savedFontSize = localStorage.getItem(FONT_SIZE_STORAGE_KEY);
    return Object.values(FONT_SIZE_OPTIONS).includes(savedFontSize)
      ? savedFontSize
      : FONT_SIZE_OPTIONS.MEDIUM;
  });

  const currentMode = darkMode ? THEME_MODES.DARK : THEME_MODES.LIGHT;
  const fontSizePixels = {
    [FONT_SIZE_OPTIONS.TINY]: "12px",
    [FONT_SIZE_OPTIONS.SMALL]: "14px",
    [FONT_SIZE_OPTIONS.MEDIUM]: "16px",
    [FONT_SIZE_OPTIONS.LARGE]: "18px",
  };

  const muiTheme = useMemo(
    () => createAppTheme(currentMode, fontSize),
    [currentMode, fontSize],
  );

  useEffect(() => {
    localStorage.setItem(THEME_STORAGE_KEY, currentMode);
    document.documentElement.setAttribute("data-theme", currentMode);
  }, [currentMode]);

  useEffect(() => {
    localStorage.setItem(FONT_SIZE_STORAGE_KEY, fontSize);
    document.documentElement.style.fontSize =
      fontSizePixels[fontSize] || fontSizePixels[FONT_SIZE_OPTIONS.MEDIUM];
  }, [fontSize]);

  const toggleTheme = () => {
    setDarkMode((prev) => !prev);
  };

  const setMode = (mode) => {
    setDarkMode(mode === THEME_MODES.DARK);
  };

  const value = {
    darkMode,
    mode: currentMode,
    setDarkMode,
    setMode,
    toggleTheme,
    fontSize,
    setFontSize,
    fontSizeOptions: FONT_SIZE_OPTIONS,
  };

  return (
    <ThemeContext.Provider value={value}>
      <MuiThemeProvider theme={muiTheme}>
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

export default ThemeContext;
